import _ from 'lodash'
import type {
	Data,
	DeepStructure,
	Language,
	SlotsData,
	Structure,
	DataMapName,
	ViewMode,
} from '@wix/thunderbolt-becky-types'
import type { BeckyRequestPayload } from '../types/becky'
import { MASTER_PAGE_COMPONENTS_IN_RESPONSIVE } from '../constants'
import { flattenStructure, getChildrenKey } from './flattenStructure'
import oneDocFixerForPage from './oneDocTestFixer'
import { AddFreemiumBannerParams, getRootComponents } from './rootComponents'
import type { BeckyExperiments } from '@wix/thunderbolt-commons'

export type ProcessedPages = {
	data: Data
	masterPageTranslations: Record<Language, { data: Pick<Data, 'document_data'> }>
	translations: Record<Language, { data: Pick<Data, 'document_data'> }>
	pageCompId: string
	masterPageStructure: Structure
	structure: Structure
}

export const getRequestedPageCompId = (pages: BeckyRequestPayload['pages']): string =>
	_.findKey(pages, (v, k) => k !== 'masterPage') || 'masterPage'

export const mergePagesData = (pageData: Data, masterPageData: Data, isResponsive: boolean) => {
	const minimalMasterPageData = _.mapValues(masterPageData, (dataMap, mapName) => {
		if (mapName === 'document_data') {
			return _.omitBy(dataMap, (dataItem) => dataItem.type === 'Anchor')
		}
		if (
			isResponsive &&
			(mapName === 'component_properties' || mapName === 'connections_data' || mapName === 'behaviors_data')
		) {
			return {}
		}

		return dataMap
	})

	const onlyPageMaps = _.pickBy(pageData, (dataMap: any, mapName: DataMapName) => !minimalMasterPageData[mapName])
	return _.reduce(
		minimalMasterPageData,
		(acc, dataMap, mapName) => {
			acc[mapName] = {
				...dataMap,
				...pageData[mapName as DataMapName],
			}
			return acc
		},
		onlyPageMaps
	) as Data
}

const mergeTranslations = (
	pageTranslations: ProcessedPages['translations'],
	masterPageTranslations: ProcessedPages['translations']
): ProcessedPages['translations'] => {
	return _.merge({}, masterPageTranslations, pageTranslations)
}

const getResponsiveCleanMasterPageStructure = (
	structure: DeepStructure,
	isMobile: boolean,
	slotsData: SlotsData,
	isDataBindingInMasterResponsive: boolean
) => {
	const childrenKey = getChildrenKey(structure, isMobile)
	const children = _.filter(
		structure[childrenKey],
		(val) =>
			MASTER_PAGE_COMPONENTS_IN_RESPONSIVE.includes(val.id) ||
			(isDataBindingInMasterResponsive && val.componentType === 'platform.components.AppController')
	)

	return flattenStructure({ ...structure, [childrenKey]: children }, isMobile, slotsData)
}

type PreparePageParams = {
	pages: BeckyRequestPayload['pages']
	shouldRenderFreemiumBanner: boolean
	viewMode: ViewMode
	isResponsive: boolean
	experiments: BeckyExperiments
} & AddFreemiumBannerParams

export const getMergedDataWithFlatPageStructure = (params: PreparePageParams): Promise<ProcessedPages> => {
	const { pages, viewMode, isResponsive, experiments } = params
	const pageCompId = getRequestedPageCompId(pages)

	return Promise.all([pages.masterPage, pages[pageCompId]]).then(([masterPage, page]) => {
		const isMasterPage = pageCompId === 'masterPage'
		const isMobile = viewMode === 'mobile'
		let data = isMasterPage ? masterPage.data : mergePagesData(page.data, masterPage.data, isResponsive)
		const isDataBindingInMasterResponsive = !!params.experiments['specs.thunderbolt.dataBindingInMasterResponsive']
		let structure =
			isMasterPage && isResponsive
				? getResponsiveCleanMasterPageStructure(
						page.structure,
						isMobile,
						data.slots,
						isDataBindingInMasterResponsive
				  )
				: flattenStructure(page.structure, isMobile, data.slots)
		const masterPageStructure = flattenStructure(masterPage.structure, isMobile, data.slots)

		if (experiments['specs.thunderbolt.oneDocStrictMode']) {
			const oneDocFixedData = oneDocFixerForPage({
				structure: isMasterPage ? masterPageStructure : structure,
				data,
			})

			structure = oneDocFixedData.structure
			data = oneDocFixedData.data
		}

		const translations = isMasterPage
			? masterPage.translations
			: mergeTranslations(page.translations, masterPage.translations)
		const masterPageTranslations = masterPage.translations

		// Add root components to master page
		if (isMasterPage) {
			structure = Object.assign(structure, getRootComponents(params))
		}

		return {
			masterPageStructure,
			structure,
			data,
			pageCompId,
			translations,
			masterPageTranslations,
		}
	})
}
